// The practice account's engine (PracticePage.js): a past day's 1-minute candles replayed as a
// moving price, market orders filled at that price, a stop and a target that close the position,
// and the account's rules (max loss, daily loss) that end the session like a real account.
// Pure functions on plain objects, so the page only keeps the state and draws it.
import { CONTRACTS } from './tools/contracts';
import { PRACTICE_INSTRUMENTS } from './practiceDays';

// The contracts the practice account trades (index futures: the ones TopstepX has candles for).
export const PRACTICE_SYMBOLS = ['ES', 'MES', 'NQ', 'MNQ', 'YM', 'MYM', 'RTY', 'M2K'];
const MICROS = new Set(['MES', 'MNQ', 'MYM', 'M2K', 'MMX', 'MNX']);

// Our practice instruments (practiceDays.js) pay like the contract they copy: MX like ES, and so on.
export const contractOf = (symbol) => {
  const fake = PRACTICE_INSTRUMENTS[symbol];
  if (fake) return { ...CONTRACTS.find((c) => c.symbol === fake.like), symbol, name: fake.name };
  return CONTRACTS.find((c) => c.symbol === symbol) || CONTRACTS.find((c) => c.symbol === 'ES');
};
// Round-trip commission for one contract (an estimate, like most prop firm platforms charge).
export const roundTripFee = (symbol) => (MICROS.has(symbol) ? 0.74 : 2.8);
const roundTo = (price, tick) => Math.round(Math.round(price / tick) * tick * 1e6) / 1e6;

// The path a 1-minute candle's price takes, `steps` points from the open to the close: through
// the low first on an up candle and the high first on a down one (the usual guess, since a
// candle only tells its open, high, low and close).
export function candlePath(bar, tick, steps = 60) {
  const up = bar.c >= bar.o;
  const legs = up ? [bar.o, bar.l, bar.h, bar.c] : [bar.o, bar.h, bar.l, bar.c];
  const lengths = [1, 2, 3].map((i) => Math.abs(legs[i] - legs[i - 1]));
  const total = lengths.reduce((s, n) => s + n, 0);
  const path = [];
  for (let k = 0; k < steps; k += 1) {
    const f = steps === 1 ? 1 : k / (steps - 1);
    if (!total) { path.push(roundTo(bar.c, tick)); continue; }
    let along = f * total;
    let i = 0;
    while (i < 2 && along > lengths[i]) { along -= lengths[i]; i += 1; }
    const part = lengths[i] ? Math.min(along / lengths[i], 1) : 1;
    path.push(roundTo(legs[i] + (legs[i + 1] - legs[i]) * part, tick));
  }
  return path;
}

// Candles of `unit` minutes from 1-minute ones, the last one possibly still forming.
export function groupBars(bars, unit) {
  if (unit === 1) return bars.slice();
  const step = unit * 60;
  const out = [];
  for (const b of bars) {
    const t = Math.floor(b.t / step) * step;
    const last = out[out.length - 1];
    if (last && last.t === t) {
      last.h = Math.max(last.h, b.h); last.l = Math.min(last.l, b.l); last.c = b.c; last.v = (last.v || 0) + (b.v || 0);
    } else out.push({ ...b, t });
  }
  return out;
}

// A new practice account. `rules`: { balance, maxLoss, trailing ('none' | 'intraday'), dailyLoss },
// plus `initial` and `peak` for an account carried over from an earlier day (the 30-day
// challenge): the starting balance max loss counts from, and the best balance so far.
export function newAccount(symbol, rules) {
  const balance = Number(rules.balance) || 50000;
  return {
    symbol, start: balance, initial: Number(rules.initial) || balance, rules: { maxLoss: Number(rules.maxLoss) || 0, trailing: rules.trailing || 'none', dailyLoss: Number(rules.dailyLoss) || 0 },
    qty: 0, avg: 0, realized: 0, fees: 0, peak: Math.max(balance, Number(rules.peak) || 0), stop: null, target: null,
    fills: [], trades: [], open: null, blown: null, orders: [],
  };
}

export const pointValue = (acct) => contractOf(acct.symbol).point;
export const unrealized = (acct, price) => (acct.qty ? (price - acct.avg) * acct.qty * pointValue(acct) : 0);
export const equity = (acct, price) => acct.start + acct.realized - acct.fees + unrealized(acct, price);

// The balance the account can't go under: the starting balance minus max loss, trailing the
// best equity up (until it reaches the starting balance) when the drawdown trails.
export function maxLossLevel(acct) {
  const { maxLoss, trailing } = acct.rules;
  if (!maxLoss) return null;
  const initial = acct.initial ?? acct.start;
  const base = trailing === 'intraday' ? Math.min(acct.peak, initial + maxLoss) : initial;
  return base - maxLoss;
}
export const dailyLossLevel = (acct) => (acct.rules.dailyLoss ? acct.start - acct.rules.dailyLoss : null);

// The price at which the open position would hit the closest rule, for a line on the chart.
export function breachPrice(acct) {
  if (!acct.qty) return null;
  const floors = [maxLossLevel(acct), dailyLossLevel(acct)].filter((n) => n !== null);
  if (!floors.length) return null;
  const floor = Math.max(...floors);
  const flat = acct.start + acct.realized - acct.fees;
  return acct.avg + (floor - flat) / (acct.qty * pointValue(acct));
}

function fill(acct, side, size, price, time, why) {
  const tick = contractOf(acct.symbol).tick;
  const px = roundTo(price, tick);
  const signed = side === 'Buy' ? size : -size;
  const next = { ...acct, fills: [...acct.fills, { side, size, price: px, time, why }], fees: acct.fees + (roundTripFee(acct.symbol) / 2) * size };
  let { qty, avg, realized, open, trades } = next;
  if (!qty || Math.sign(qty) === Math.sign(signed)) {
    // Opening or adding: the average price moves.
    avg = (avg * Math.abs(qty) + px * size) / (Math.abs(qty) + size);
    qty += signed;
    open = open ? { ...open, size: Math.max(open.size, Math.abs(qty)), entryPrice: avg } : { side: signed > 0 ? 'Long' : 'Short', entryTime: time, size: Math.abs(qty), entryPrice: avg };
  } else {
    // Closing (and maybe reversing): the closed part books its P&L.
    const closing = Math.min(Math.abs(qty), size);
    const pnl = (px - avg) * closing * Math.sign(qty) * pointValue(next);
    realized += pnl;
    open = { ...open, pnl: (open.pnl || 0) + pnl, closed: (open.closed || 0) + closing, exitPrice: px, exitTime: time };
    const left = qty + signed;
    if (Math.sign(left) !== Math.sign(qty)) {
      // Flat (or reversed): the trade is done.
      const fees = roundTripFee(acct.symbol) * open.size;
      trades = [...trades, {
        side: open.side, size: open.size, entryPrice: roundTo(open.entryPrice, tick), exitPrice: px, entryTime: open.entryTime, exitTime: time,
        pnl: open.pnl, fees, net: open.pnl - fees, stopPrice: open.stopPrice ?? null, targetPrice: open.targetPrice ?? null, planned: Boolean(open.planned), exit: why,
      }];
      open = left ? { side: left > 0 ? 'Long' : 'Short', entryTime: time, size: Math.abs(left), entryPrice: px } : null;
      avg = left ? px : 0;
      next.stop = null; next.target = null;
    }
    qty = left;
  }
  return { ...next, qty, avg, realized, open, trades };
}

// Fills an entry at `px` and puts the stop and target `stopTicks` / `targetTicks` from the
// average price (when given). The trade remembers its first stop and target, for its risk (R).
function enter(acct, side, size, px, time, why, { stopTicks = 0, targetTicks = 0 } = {}) {
  const tick = contractOf(acct.symbol).tick;
  let next = fill(acct, side, size, px, time, why);
  // A new position remembers whether its stop and target went in with the entry (planned).
  if (!acct.qty && next.open) next = { ...next, open: { ...next.open, planned: stopTicks > 0 && targetTicks > 0 } };
  if (next.qty && (stopTicks || targetTicks)) {
    const dir = Math.sign(next.qty);
    next = {
      ...next,
      stop: stopTicks ? roundTo(next.avg - dir * stopTicks * tick, tick) : next.stop,
      target: targetTicks ? roundTo(next.avg + dir * targetTicks * tick, tick) : next.target,
    };
  }
  return remember(next);
}
function remember(next) {
  if (next.open && next.open.stopPrice === undefined && next.stop !== null) next = { ...next, open: { ...next.open, stopPrice: next.stop } };
  if (next.open && next.open.targetPrice === undefined && next.target !== null) next = { ...next, open: { ...next.open, targetPrice: next.target } };
  return next;
}

// A market order: fills at the price plus `slip` ticks against the trader. A stop and a target,
// in ticks from the fill, close the whole position when price reaches them.
export function marketOrder(acct, side, size, price, time, { slip = 1, stopTicks = 0, targetTicks = 0 } = {}) {
  if (acct.blown || !size) return acct;
  const tick = contractOf(acct.symbol).tick;
  return enter(acct, side, size, price + (side === 'Buy' ? slip : -slip) * tick, time, 'market', { stopTicks, targetTicks });
}

// A working order: a limit (buy at or under its price, sell at or over it) or a stop entry (buy
// once price rises to it, sell once it falls to it), with the stop and target to put on its fill.
let orderIds = 0;
export function placeOrder(acct, { type, side, size, price, stopTicks = 0, targetTicks = 0 }) {
  if (acct.blown || !size || !Number.isFinite(price)) return acct;
  const tick = contractOf(acct.symbol).tick;
  orderIds += 1;
  return { ...acct, orders: [...(acct.orders || []), { id: `o${orderIds}`, type, side, size, price: roundTo(price, tick), stopTicks, targetTicks }] };
}
export const cancelOrder = (acct, id) => ({ ...acct, orders: (acct.orders || []).filter((o) => o.id !== id) });
export const moveOrder = (acct, id, price) => ({
  ...acct, orders: (acct.orders || []).map((o) => (o.id === id ? { ...o, price: roundTo(price, contractOf(acct.symbol).tick) } : o)),
});

export function flatten(acct, price, time, why = 'flatten') {
  if (!acct.qty) return acct;
  const tick = contractOf(acct.symbol).tick;
  const side = acct.qty > 0 ? 'Sell' : 'Buy';
  const px = why === 'flatten' ? price + (side === 'Buy' ? 1 : -1) * tick : price;
  return fill(acct, side, Math.abs(acct.qty), px, time, why);
}

// Moves (or adds, or with null removes) the position's stop and target.
export function setBracket(acct, { stop, target }) {
  if (!acct.qty) return acct;
  const tick = contractOf(acct.symbol).tick;
  const snap = (v) => (v === null ? null : roundTo(v, tick));
  return remember({ ...acct, ...(stop !== undefined ? { stop: snap(stop) } : {}), ...(target !== undefined ? { target: snap(target) } : {}) });
}

// Price moved: working orders fill when reached, then the stop or target (a stop fills at the
// worse of its price and the market when price jumped past it); the best equity is remembered,
// and an account at or under a rule's level is closed out and ended.
export function onPrice(acct, price, time) {
  if (acct.blown) return acct;
  let next = acct;
  const tick = contractOf(acct.symbol).tick;
  for (const o of next.orders || []) {
    const buy = o.side === 'Buy';
    const hit = o.type === 'limit' ? (buy ? price <= o.price : price >= o.price) : (buy ? price >= o.price : price <= o.price);
    if (!hit) continue;
    const px = o.type === 'limit' ? o.price : (buy ? Math.max(o.price, price) + tick : Math.min(o.price, price) - tick);
    next = enter(cancelOrder(next, o.id), o.side, o.size, px, time, o.type === 'stop' ? 'stopEntry' : o.type, { stopTicks: o.stopTicks, targetTicks: o.targetTicks });
  }
  if (next.qty) {
    const long = next.qty > 0;
    if (next.stop !== null && (long ? price <= next.stop : price >= next.stop)) next = flatten(next, long ? Math.min(next.stop, price) : Math.max(next.stop, price), time, 'stop');
    else if (next.target !== null && (long ? price >= next.target : price <= next.target)) next = flatten(next, next.target, time, 'target');
  }
  const eq = equity(next, price);
  if (eq > next.peak) next = { ...next, peak: eq };
  const checks = [['maxLoss', maxLossLevel(next)], ['dailyLoss', dailyLossLevel(next)]];
  const hit = checks.find(([, level]) => level !== null && eq <= level);
  if (hit) {
    next = flatten(next, price, time, hit[0]);
    next = { ...next, orders: [], blown: { rule: hit[0], time, equity: equity(next, price) } };
  }
  return next;
}
